import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { 
  PermissionOverride, 
  UserPermissionSummary, 
  RolePermissionMatrix, 
  AvailablePermissions,
  Permission 
} from '../../types';
import apiService from '../../services/api';

interface PermissionsState {
  // User permission overrides
  userPermissionOverrides: { [userId: string]: PermissionOverride[] };
  userPermissionSummaries: { [userId: string]: UserPermissionSummary };
  
  // Role permission matrices
  rolePermissionMatrices: { [role: string]: RolePermissionMatrix };
  
  // Available permissions for UI dropdowns
  availablePermissions: AvailablePermissions | null;
  
  // Cache performance metrics
  cacheMetrics: {
    hitRate: number;
    averageQueryTime: number;
    totalQueries: number;
    cacheSize: number;
  } | null;
  
  // Loading states
  isLoading: boolean;
  isLoadingUserPermissions: { [userId: string]: boolean };
  isLoadingRolePermissions: { [role: string]: boolean };
  isSaving: boolean;
  
  // Error states
  error: string | null;
  userPermissionErrors: { [userId: string]: string | null };
  rolePermissionErrors: { [role: string]: string | null };
  
  // Bulk operation states
  bulkOperationProgress: {
    isActive: boolean;
    total: number;
    completed: number;
    failed: number;
    errors: string[];
  };
  
  // Last update timestamps for cache invalidation
  lastUserPermissionUpdate: { [userId: string]: number };
  lastRolePermissionUpdate: { [role: string]: number };
  lastAvailablePermissionsUpdate: number | null;
}

const initialState: PermissionsState = {
  userPermissionOverrides: {},
  userPermissionSummaries: {},
  rolePermissionMatrices: {},
  availablePermissions: null,
  cacheMetrics: null,
  isLoading: false,
  isLoadingUserPermissions: {},
  isLoadingRolePermissions: {},
  isSaving: false,
  error: null,
  userPermissionErrors: {},
  rolePermissionErrors: {},
  bulkOperationProgress: {
    isActive: false,
    total: 0,
    completed: 0,
    failed: 0,
    errors: []
  },
  lastUserPermissionUpdate: {},
  lastRolePermissionUpdate: {},
  lastAvailablePermissionsUpdate: null,
};

// Async thunks for permission management
export const fetchUserPermissionSummary = createAsyncThunk(
  'permissions/fetchUserPermissionSummary',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/users/${userId}/permissions`);
      return { userId, summary: response.results };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user permissions');
    }
  }
);

export const fetchRolePermissions = createAsyncThunk(
  'permissions/fetchRolePermissions',
  async (role: string, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/users/roles/${role}/permissions`);
      return { role, permissions: response.results };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch role permissions');
    }
  }
);

export const fetchAvailablePermissions = createAsyncThunk(
  'permissions/fetchAvailablePermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/auth/available-permissions');
      return response.results;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch available permissions');
    }
  }
);

export const grantPermissionOverride = createAsyncThunk(
  'permissions/grantPermissionOverride',
  async ({ 
    userId, 
    override 
  }: { 
    userId: string; 
    override: Omit<PermissionOverride, 'grantedAt' | 'grantedBy'> 
  }, { rejectWithValue }) => {
    try {
      const response = await apiService.post(`/users/${userId}/permission-overrides`, override);
      return { userId, override: response.results };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to grant permission override');
    }
  }
);

export const revokePermissionOverride = createAsyncThunk(
  'permissions/revokePermissionOverride',
  async ({ 
    userId, 
    resource, 
    action, 
    scope 
  }: { 
    userId: string; 
    resource: string; 
    action: string; 
    scope: string; 
  }, { rejectWithValue }) => {
    try {
      await apiService.delete(`/users/${userId}/permission-overrides`, {
        data: { resource, action, scope }
      });
      return { userId, resource, action, scope };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to revoke permission override');
    }
  }
);

export const bulkGrantPermissionOverrides = createAsyncThunk(
  'permissions/bulkGrantPermissionOverrides',
  async ({ 
    userIds, 
    override 
  }: { 
    userIds: string[]; 
    override: Omit<PermissionOverride, 'grantedAt' | 'grantedBy'> 
  }, { rejectWithValue, dispatch }) => {
    try {
      // Initialize bulk operation progress
      dispatch(setBulkOperationProgress({
        isActive: true,
        total: userIds.length,
        completed: 0,
        failed: 0,
        errors: []
      }));

      const response = await apiService.post('/users/bulk/permission-overrides', {
        userIds,
        override
      });

      return { userIds, override: response.results };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk grant permission overrides');
    }
  }
);

export const bulkRevokePermissionOverrides = createAsyncThunk(
  'permissions/bulkRevokePermissionOverrides',
  async ({ 
    userIds, 
    resource, 
    action, 
    scope 
  }: { 
    userIds: string[]; 
    resource: string; 
    action: string; 
    scope: string; 
  }, { rejectWithValue, dispatch }) => {
    try {
      // Initialize bulk operation progress
      dispatch(setBulkOperationProgress({
        isActive: true,
        total: userIds.length,
        completed: 0,
        failed: 0,
        errors: []
      }));

      await apiService.delete('/users/bulk/permission-overrides', {
        data: { userIds, resource, action, scope }
      });

      return { userIds, resource, action, scope };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk revoke permission overrides');
    }
  }
);

export const fetchCacheMetrics = createAsyncThunk(
  'permissions/fetchCacheMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/users/cache/metrics');
      return response.results;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cache metrics');
    }
  }
);

export const warmupPermissionCache = createAsyncThunk(
  'permissions/warmupPermissionCache',
  async ({ 
    userIds, 
    roles 
  }: { 
    userIds?: string[]; 
    roles?: string[]; 
  }, { rejectWithValue }) => {
    try {
      const response = await apiService.post('/users/cache/warmup', {
        userIds,
        roles
      });
      return response.results;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to warmup permission cache');
    }
  }
);

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUserPermissionError: (state, action: PayloadAction<string>) => {
      delete state.userPermissionErrors[action.payload];
    },
    clearRolePermissionError: (state, action: PayloadAction<string>) => {
      delete state.rolePermissionErrors[action.payload];
    },
    setBulkOperationProgress: (state, action: PayloadAction<Partial<PermissionsState['bulkOperationProgress']>>) => {
      state.bulkOperationProgress = { ...state.bulkOperationProgress, ...action.payload };
    },
    resetBulkOperationProgress: (state) => {
      state.bulkOperationProgress = {
        isActive: false,
        total: 0,
        completed: 0,
        failed: 0,
        errors: []
      };
    },
    invalidateUserPermissionCache: (state, action: PayloadAction<string>) => {
      delete state.lastUserPermissionUpdate[action.payload];
      delete state.userPermissionSummaries[action.payload];
      delete state.userPermissionOverrides[action.payload];
    },
    invalidateRolePermissionCache: (state, action: PayloadAction<string>) => {
      delete state.lastRolePermissionUpdate[action.payload];
      delete state.rolePermissionMatrices[action.payload];
    },
    invalidateAllPermissionCaches: (state) => {
      state.userPermissionSummaries = {};
      state.userPermissionOverrides = {};
      state.rolePermissionMatrices = {};
      state.lastUserPermissionUpdate = {};
      state.lastRolePermissionUpdate = {};
      state.lastAvailablePermissionsUpdate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user permission summary
      .addCase(fetchUserPermissionSummary.pending, (state, action) => {
        state.isLoadingUserPermissions[action.meta.arg] = true;
        delete state.userPermissionErrors[action.meta.arg];
      })
      .addCase(fetchUserPermissionSummary.fulfilled, (state, action) => {
        const { userId, summary } = action.payload;
        state.isLoadingUserPermissions[userId] = false;
        state.userPermissionSummaries[userId] = summary;
        state.userPermissionOverrides[userId] = summary.permissionOverrides;
        state.lastUserPermissionUpdate[userId] = Date.now();
      })
      .addCase(fetchUserPermissionSummary.rejected, (state, action) => {
        const userId = action.meta.arg;
        state.isLoadingUserPermissions[userId] = false;
        state.userPermissionErrors[userId] = action.payload as string;
      })
      
      // Fetch role permissions
      .addCase(fetchRolePermissions.pending, (state, action) => {
        state.isLoadingRolePermissions[action.meta.arg] = true;
        delete state.rolePermissionErrors[action.meta.arg];
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        const { role, permissions } = action.payload;
        state.isLoadingRolePermissions[role] = false;
        state.rolePermissionMatrices[role] = {
          role,
          permissions,
          isCustom: false
        };
        state.lastRolePermissionUpdate[role] = Date.now();
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        const role = action.meta.arg;
        state.isLoadingRolePermissions[role] = false;
        state.rolePermissionErrors[role] = action.payload as string;
      })
      
      // Fetch available permissions
      .addCase(fetchAvailablePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailablePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availablePermissions = action.payload;
        state.lastAvailablePermissionsUpdate = Date.now();
      })
      .addCase(fetchAvailablePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearUserPermissionError,
  clearRolePermissionError,
  setBulkOperationProgress,
  resetBulkOperationProgress,
  invalidateUserPermissionCache,
  invalidateRolePermissionCache,
  invalidateAllPermissionCaches,
} = permissionsSlice.actions;

export default permissionsSlice.reducer;
